/**
 * Mixpanel (EU).
 * Variables : VUE_APP_MIXPANEL_TOKEN, VUE_APP_MIXPANEL_API_HOST, VUE_APP_MIXPANEL_ENABLED,
 * VUE_APP_MIXPANEL_IGNORE_DNT (défaut true), VUE_APP_MIXPANEL_DEBUG,
 * VUE_APP_MIXPANEL_ALLOW_DEV (true = activer aussi en dev, défaut désactivé ; prod toujours OK si ENABLED).
 * VUE_APP_MIXPANEL_RECORD_SESSIONS_PERCENT (0–100 ; défaut 0 = Session Replay désactivé).
 *
 * Le SDK s’initialise au chargement si ENABLED + token (sans attendre le cookie cookies-consent).
 * Matomo / LinkedIn Insight restent pilotés par la bannière cookies.
 */
import Cookies from 'js-cookie';
import router from '../router.js';
import { isLightBootPublicPath } from '../utils/marketingPages.js';

const TOKEN = (process.env.VUE_APP_MIXPANEL_TOKEN || '').trim();
const IS_PROD = process.env.NODE_ENV === 'production';
/** En dev, Mixpanel est désactivé sauf si explicitement autorisé (évite de polluer les stats / prod). */
const ALLOW_IN_DEV =
  String(process.env.VUE_APP_MIXPANEL_ALLOW_DEV ?? '').toLowerCase() === 'true';
const ENABLED =
  (IS_PROD || ALLOW_IN_DEV) &&
  String(process.env.VUE_APP_MIXPANEL_ENABLED ?? 'true').toLowerCase() !== 'false';
const API_HOST =
  (process.env.VUE_APP_MIXPANEL_API_HOST || 'https://api-eu.mixpanel.com').trim();
const IS_DEV = process.env.NODE_ENV === 'development';
/** DNT navigateur = opt-out Mixpanel par défaut ; true = ignorer DNT (recommandé back-office). */
const IGNORE_DNT =
  String(process.env.VUE_APP_MIXPANEL_IGNORE_DNT ?? 'true').toLowerCase() === 'true';
/** Logs SDK ([batch], [autocapture], etc.) — uniquement si VUE_APP_MIXPANEL_DEBUG=true (pas en dev par défaut). */
const DEBUG =
  String(process.env.VUE_APP_MIXPANEL_DEBUG ?? '').toLowerCase() === 'true';

/** True après `mixpanel.init` réussi (`getMixpanel`, etc.). */
let _mp = null;
let initialized = false;
let lastIdentifiedDistinctId = null;
let lastPeopleSetSignature = null;
let lastSubscriptionEventSignature = null;
const ATTRIBUTION_STORAGE_KEY = 'mixpanel_attribution_snapshot_v1';
const PROFILE_BOOTSTRAP_SYNC_STORAGE_KEY = 'mixpanel_profile_bootstrap_sync_v1';
const ATTRIBUTION_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'ref',
];

function normalizePath(path = '') {
  const source =
    path ||
    (typeof window !== 'undefined'
      ? window.location.pathname + window.location.search
      : '/');
  try {
    return new URL(String(source), 'https://studio.mydid.local').pathname;
  } catch (_err) {
    return String(source || '/').split('?')[0] || '/';
  }
}

function isLightPublicRoute(path = '') {
  return isLightBootPublicPath(normalizePath(path));
}

/** Lecture robuste du cookie consent (vue-cookies / js-cookie / document.cookie). */
function getConsentCookieRaw() {
  if (typeof document === 'undefined') return null;
  let v = Cookies.get('consent');
  if (v !== undefined && v !== null && v !== '') return String(v);
  const m = document.cookie.match(/(?:^|;\s*)consent=([^;]*)/);
  if (!m) return null;
  try {
    return decodeURIComponent(m[1].trim());
  } catch (_) {
    return m[1];
  }
}

/** Cookie cookies-consent (Matomo, etc.) ; Mixpanel ne dépend plus de cette valeur. */
export function isAnalyticsConsentGranted() {
  const c = getConsentCookieRaw();
  if (c == null || c === '') return false;
  const s = String(c).trim().toLowerCase();
  return s === '1' || s === 'true' || s === 'yes' || s === 'on';
}

/**
 * Initialise une seule fois le SDK (ENABLED + token).
 * @returns {import('mixpanel-browser').Mixpanel | null}
 */
async function doMixpanelInit(options = {}) {
  if (!ENABLED || !TOKEN) return null;
  if (!options.allowPublicLightBootPath && isLightPublicRoute(options.path)) return null;
  if (initialized) return _mp;

  const { default: mixpanel } = await import(/* webpackChunkName: "analytics-mixpanel" */ 'mixpanel-browser');
  _mp = mixpanel;

  _mp.init(TOKEN, {
    autocapture: true,
    record_sessions_percent: parseInt(process.env.VUE_APP_MIXPANEL_RECORD_SESSIONS_PERCENT ?? '0', 10),
    api_host: API_HOST,
    persistence: 'localStorage',
    debug: DEBUG,
    ignore_dnt: IGNORE_DNT,
  });
  initialized = true;
  _mp.opt_in_tracking();

  if (DEBUG && TOKEN) {
    // eslint-disable-next-line no-console
    console.info('[Mixpanel] init OK', {
      api_host: API_HOST,
      ignore_dnt: IGNORE_DNT,
    });
  }

  return _mp;
}

/**
 * Appelé depuis main.js au démarrage (sans condition cookies-consent).
 * @returns {import('mixpanel-browser').Mixpanel | null}
 */
export async function initMixpanel() {
  if (!ENABLED || !TOKEN) {
    if (IS_DEV && ALLOW_IN_DEV && !TOKEN) {
      // eslint-disable-next-line no-console
      console.warn('[Mixpanel] VUE_APP_MIXPANEL_TOKEN manquant — aucun envoi (rebuild après .env).');
    }
    return null;
  }
  return await doMixpanelInit({ allowPublicLightBootPath: true });
}

export function getMixpanel() {
  return initialized ? _mp : null;
}

function sanitizeAttributionValue(value) {
  if (value === null || value === undefined) return null;
  const trimmed = String(value).trim();
  if (!trimmed) return null;
  return trimmed.slice(0, 255);
}

function compactEventProps(payload = {}) {
  return Object.entries(payload || {}).reduce((acc, [key, value]) => {
    if (value === null || value === undefined || value === '') return acc;
    acc[key] = value;
    return acc;
  }, {});
}

function readStoredAttributionSnapshot() {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(ATTRIBUTION_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return parsed;
  } catch (_err) {
    return null;
  }
}

function writeStoredAttributionSnapshot(snapshot) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(
      ATTRIBUTION_STORAGE_KEY,
      JSON.stringify(snapshot || {})
    );
  } catch (_err) {
    // localStorage may be unavailable (privacy mode), ignore silently.
  }
}

function buildAttributionFromSearch(search) {
  const source = typeof search === 'string' ? search : '';
  const params = new URLSearchParams(source.startsWith('?') ? source : `?${source}`);
  const values = {};
  for (const key of ATTRIBUTION_KEYS) {
    const cleaned = sanitizeAttributionValue(params.get(key));
    if (cleaned) values[key] = cleaned;
  }
  return values;
}

export function getStoredAttributionPayload() {
  const snapshot = readStoredAttributionSnapshot() || {};
  const firstTouch = snapshot.firstTouch || {};
  const lastTouch = snapshot.lastTouch || {};
  return {
    attribution_source: lastTouch.utm_source || firstTouch.utm_source || null,
    attribution_medium: lastTouch.utm_medium || firstTouch.utm_medium || null,
    attribution_campaign:
      lastTouch.utm_campaign || firstTouch.utm_campaign || null,
    attribution_content:
      lastTouch.utm_content || firstTouch.utm_content || null,
    attribution_term: lastTouch.utm_term || firstTouch.utm_term || null,
    ref_link_id: lastTouch.ref || firstTouch.ref || null,
    attribution_landing_path: firstTouch.landingPath || null,
    attribution_landing_url: firstTouch.landingUrl || null,
    attribution_first_seen_at: firstTouch.capturedAt || null,
    attribution_last_seen_at: lastTouch.capturedAt || null,
    /** Referrer HTTP au chargement (ex. LinkedIn, résolveur de lien) si transmis par le navigateur */
    attribution_document_referrer:
      lastTouch.documentReferrer || firstTouch.documentReferrer || null,
  };
}

export function captureAttributionFromCurrentUrl() {
  if (typeof window === 'undefined') return;
  const extracted = buildAttributionFromSearch(window.location.search || '');
  const docRef =
    typeof document !== 'undefined' && document.referrer
      ? sanitizeAttributionValue(document.referrer)
      : null;
  const nowIso = new Date().toISOString();
  const existingSnapshot = readStoredAttributionSnapshot() || {};
  const hadFirstTouch =
    existingSnapshot.firstTouch &&
    Object.keys(existingSnapshot.firstTouch).length > 0;
  const hasMarketingParams = Object.keys(extracted).length > 0;

  if (!hasMarketingParams && !hadFirstTouch) {
    const firstTouch = {
      capturedAt: nowIso,
      landingPath: window.location.pathname + window.location.search,
      landingUrl: window.location.href,
      ...(docRef ? { documentReferrer: docRef } : {}),
    };
    const lastTouch = { ...firstTouch };
    writeStoredAttributionSnapshot({ firstTouch, lastTouch });
  } else if (hasMarketingParams) {
    const firstTouch =
      hadFirstTouch && existingSnapshot.firstTouch
        ? existingSnapshot.firstTouch
        : {
            ...extracted,
            capturedAt: nowIso,
            landingPath: window.location.pathname + window.location.search,
            landingUrl: window.location.href,
            ...(docRef ? { documentReferrer: docRef } : {}),
          };

    const lastTouch = {
      ...extracted,
      capturedAt: nowIso,
      landingPath: window.location.pathname + window.location.search,
      landingUrl: window.location.href,
      ...(docRef ? { documentReferrer: docRef } : {}),
    };

    writeStoredAttributionSnapshot({
      firstTouch,
      lastTouch,
    });
  } else {
    const lastTouch = {
      ...(existingSnapshot.lastTouch || {}),
      capturedAt: nowIso,
      landingPath: window.location.pathname + window.location.search,
      landingUrl: window.location.href,
      ...(docRef ? { documentReferrer: docRef } : {}),
    };
    writeStoredAttributionSnapshot({
      firstTouch: existingSnapshot.firstTouch || {},
      lastTouch,
    });
  }

  const snapshotAfter = readStoredAttributionSnapshot() || {};
  const shouldEmitAttributionEvent =
    hasMarketingParams ||
    (!hadFirstTouch &&
      snapshotAfter.firstTouch &&
      Object.keys(snapshotAfter.firstTouch).length > 0);

  if (!shouldEmitAttributionEvent || !lastIdentifiedDistinctId) return;

  trackMixpanelEvent(
    'Attribution Captured',
    {
      ...extracted,
      ...(docRef ? { document_referrer: docRef } : {}),
      landing_path: window.location.pathname + window.location.search,
      landing_url: window.location.href,
      captured_at: nowIso,
    },
    {
      session: { studioAccountId: lastIdentifiedDistinctId },
      requireDistinctId: true,
      attachContext: true,
    }
  );
}

/**
 * Après « Accepter », router.afterEach ne se rejoue pas pour la page courante :
 * on envoie tout de suite une vue de page, sinon aucun event tant que l’utilisateur ne navigue pas.
 */
function trackCurrentRoutePageView() {
  if (!lastIdentifiedDistinctId) return;
  try {
    const to = router.currentRoute.value;
    const path = to?.fullPath || (typeof window !== 'undefined' ? window.location.pathname + window.location.search : '');
    const name = to?.name != null ? String(to.name) : '';
    trackMixpanelEvent(
      'Page View',
      { path, name },
      {
        session: { studioAccountId: lastIdentifiedDistinctId },
        requireDistinctId: true,
        attachContext: true,
      }
    );
    if (DEBUG) {
      // eslint-disable-next-line no-console
      console.info('[Mixpanel] Page View (post-consent)', { path, name });
    }
  } catch (e) {
    if (typeof window === 'undefined') return;
    trackMixpanelEvent(
      'Page View',
      {
        path: window.location.pathname + window.location.search,
        name: 'post-consent-fallback',
      },
      {
        session: { studioAccountId: lastIdentifiedDistinctId },
        requireDistinctId: true,
        attachContext: true,
      }
    );
  }
}

/**
 * Appelé depuis la bannière cookies : ne pilote plus l’activation de Mixpanel.
 * Conserve l’envoi d’attribution + Page View quand l’utilisateur accepte (complément post-clic).
 */
export async function applyMixpanelConsent(accepted) {
  if (!ENABLED || !TOKEN) return;

  if (accepted) {
    if (!initialized) {
      await doMixpanelInit();
    } else {
      _mp.opt_in_tracking();
    }
    if (initialized) {
      captureAttributionFromCurrentUrl();
      trackCurrentRoutePageView();
    }
  }
}

function buildStableDistinctId(session) {
  if (!session || session.isTmp) return null;
  const stableId = String(session.studioAccountId || '').trim();
  return stableId || null;
}

export function getMixpanelDistinctIdFromSession(session) {
  return buildStableDistinctId(session);
}

function buildCommunityNameFromIssuerInfo(issuerInfo = {}) {
  const candidates = [
    issuerInfo?.name,
    issuerInfo?.communityName,
    issuerInfo?.pendingName,
    issuerInfo?.adminProfile?.name,
  ];
  const picked = candidates.find(
    (value) => typeof value === 'string' && value.trim()
  );
  return picked ? picked.trim() : null;
}

export function trackMixpanelEvent(
  eventName,
  props = {},
  {
    session = null,
    issuerInfo = null,
    requireDistinctId = true,
    attachContext = true,
  } = {}
) {
  const mp = getMixpanel();
  if (!mp) return false;

  const distinctId = buildStableDistinctId(session);
  if (requireDistinctId && !distinctId) return false;

  if (distinctId && lastIdentifiedDistinctId !== distinctId) {
    mp.identify(distinctId);
    lastIdentifiedDistinctId = distinctId;
  }

  const contextProps = attachContext
    ? compactEventProps({
        distinct_id: distinctId || null,
        studio_account_id: distinctId || null,
        issuer_did: issuerInfo?.did || null,
        community_did: issuerInfo?.did || null,
        community_name: buildCommunityNameFromIssuerInfo(issuerInfo),
        session_did: session?.did || null,
        account_email:
          session?.connexionEmail ||
          issuerInfo?.currentAdmin?.email ||
          issuerInfo?.adminEmail ||
          issuerInfo?.accountProfile?.email ||
          null,
      })
    : {};

  const payload = {
    ...contextProps,
    ...(props || {}),
  };

  if (distinctId) {
    if (!payload.distinct_id) payload.distinct_id = distinctId;
    if (!payload.studio_account_id) payload.studio_account_id = distinctId;
  }

  mp.track(eventName, payload);
  return true;
}

function buildUserProfilePayload({ session, issuerInfo } = {}) {
  const email =
    session?.connexionEmail ||
    issuerInfo?.currentAdmin?.email ||
    issuerInfo?.adminEmail ||
    issuerInfo?.accountProfile?.email ||
    null;

  const accountFullName = [
    issuerInfo?.accountProfile?.firstName,
    issuerInfo?.accountProfile?.lastName,
  ]
    .filter(Boolean)
    .join(' ')
    .trim();

  const currentAdminFullName = [
    issuerInfo?.currentAdmin?.firstName,
    issuerInfo?.currentAdmin?.lastName,
  ]
    .filter(Boolean)
    .join(' ')
    .trim();

  const displayName =
    accountFullName ||
    currentAdminFullName ||
    issuerInfo?.accountProfile?.displayName ||
    issuerInfo?.currentAdmin?.name ||
    null;

  const subscription = issuerInfo?.subscription || {};
  const rawPlan = subscription?.plan || subscription?.tier || null;
  const plan = rawPlan ? String(rawPlan).trim().toLowerCase() : null;
  const subscriptionStatus = subscription?.type
    ? String(subscription?.stripeInvoiceStatus || '').trim() || (subscription?.isSubscribed ? 'active' : 'inactive')
    : 'free';
  const stripeCustomerId = subscription?.stripeCustomerId
    ? String(subscription.stripeCustomerId).trim()
    : null;

  return {
    email: email ? String(email).trim() : null,
    name: displayName ? String(displayName).trim() : null,
    community_name: buildCommunityNameFromIssuerInfo(issuerInfo),
    community_did: issuerInfo?.did ? String(issuerInfo.did).trim() : null,
    current_plan: plan,
    subscription_status: subscriptionStatus,
    stripe_customer_id: stripeCustomerId,
  };
}

export function syncMixpanelUser({ session, issuerInfo, force = false } = {}) {
  const forceSync = Boolean(force);
  const mp = getMixpanel();
  if (!mp) return;

  const distinctId = buildStableDistinctId(session);
  if (!distinctId) return;

  if (forceSync || lastIdentifiedDistinctId !== distinctId) {
    mp.identify(distinctId);
    lastIdentifiedDistinctId = distinctId;
  }

  const profile = buildUserProfilePayload({ session, issuerInfo });
  const attributionProps = compactEventProps(getStoredAttributionPayload());
  const peoplePayload = {};
  if (profile.email) peoplePayload.$email = profile.email;
  if (profile.name) peoplePayload.$name = profile.name;
  if (profile.community_name) {
    peoplePayload.current_community_name = profile.community_name;
  }
  if (profile.community_did) {
    peoplePayload.current_community_did = profile.community_did;
  }
  if (profile.current_plan) peoplePayload.current_plan = profile.current_plan;
  if (profile.subscription_status) {
    peoplePayload.subscription_status = profile.subscription_status;
  }
  if (profile.stripe_customer_id) {
    peoplePayload.stripe_customer_id = profile.stripe_customer_id;
  }
  Object.assign(peoplePayload, attributionProps);

  if (Object.keys(peoplePayload).length > 0) {
    const signature = `${distinctId}|${peoplePayload.$email || ''}|${peoplePayload.$name || ''}|${peoplePayload.current_community_name || ''}|${peoplePayload.current_community_did || ''}|${peoplePayload.current_plan || ''}|${peoplePayload.subscription_status || ''}|${peoplePayload.stripe_customer_id || ''}|${JSON.stringify(attributionProps)}`;
    if (forceSync || signature !== lastPeopleSetSignature) {
      mp.people.set(peoplePayload);
      lastPeopleSetSignature = signature;
    }
  }

  const subscriptionSnapshotPayload = {
    current_plan: profile.current_plan || null,
    subscription_status: profile.subscription_status || null,
    stripe_customer_id: profile.stripe_customer_id || null,
  };
  const hasSubscriptionSnapshotData = Object.values(subscriptionSnapshotPayload).some(
    (value) => value !== null && value !== undefined && value !== ''
  );
  if (hasSubscriptionSnapshotData) {
    const eventSignature = `${distinctId}|${subscriptionSnapshotPayload.current_plan || ''}|${subscriptionSnapshotPayload.subscription_status || ''}|${subscriptionSnapshotPayload.stripe_customer_id || ''}`;
    if (eventSignature !== lastSubscriptionEventSignature) {
      trackMixpanelEvent('Subscription Snapshot Updated', subscriptionSnapshotPayload, {
        session,
        issuerInfo,
        requireDistinctId: true,
        attachContext: true,
      });
      lastSubscriptionEventSignature = eventSignature;
    }
  }
}

function readBootstrapProfileSyncMap() {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(PROFILE_BOOTSTRAP_SYNC_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (_err) {
    return {};
  }
}

function writeBootstrapProfileSyncMap(nextMap) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(
      PROFILE_BOOTSTRAP_SYNC_STORAGE_KEY,
      JSON.stringify(nextMap || {})
    );
  } catch (_err) {
    // ignore
  }
}

export function syncMixpanelUserOncePerAccount({ session, issuerInfo } = {}) {
  const distinctId = buildStableDistinctId(session);
  if (!distinctId) return false;
  if (!getMixpanel()) return false;

  const snapshot = readBootstrapProfileSyncMap();
  if (snapshot?.[distinctId]) return false;

  syncMixpanelUser({ session, issuerInfo, force: true });

  const nowIso = new Date().toISOString();
  writeBootstrapProfileSyncMap({
    ...snapshot,
    [distinctId]: nowIso,
  });
  return true;
}
